import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "./ui/Icons";

const COMPANY_LINKS = [
  { label: "About", to: "/info" },
  { label: "Team", to: "/team" },
  { label: "Blog", to: "/blogs" },
  { label: "Partner with us", to: "/#partners" },
  { label: "Donate", to: "/donate" },
];

const RESOURCE_LINKS = [
  { label: "Try AgriHyphen", to: "/test-ai" },
  { label: "Climate Action", to: "/climate-action" },
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms of Use", to: "/terms-of-use" },
];

const CONTACT = {
  email: "hyphentechinfo@gmail.com",
  phone: "+257 62 67 42 77",
  whatsapp: "https://wa.me/25776935925",
  location: "Ngozi, Burundi",
  linkedin: "https://www.linkedin.com/company/hyphen-tech",
  website: "https://www.hyphentech.africa/",
};

const SocialLink = ({ href, label, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    className="inline-flex h-11 w-11 items-center justify-center border border-white/15 text-white/60 transition-colors duration-200 hover:border-white/40 hover:text-white"
  >
    {children}
  </a>
);

const ColumnHeading = ({ children }) => (
  <h2 className="font-mono text-[0.6875rem] font-medium uppercase tracking-[0.14em] text-white/60">
    {children}
  </h2>
);

const FooterLink = ({ to, children }) => (
  <li>
    <Link
      to={to}
      className="inline-block py-1.5 text-[0.9375rem] text-white/65 transition-colors duration-200 hover:text-agri-lime"
    >
      {children}
    </Link>
  </li>
);

export default function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="bg-agri-forestDeep text-white">
      {/* Closing call to action, carried on the footer rather than a separate
          full-height band — keeps the page from ending on a whimper. */}
      <div className="container-x border-b border-white/12 py-14 sm:py-16">
        <div className="grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
          <p className="text-balance font-display text-h2 font-semibold leading-[1.1] tracking-[-0.024em] text-white lg:col-span-8">
            Put a diagnosis in every farmer&rsquo;s pocket.
          </p>
          <div className="flex flex-wrap gap-3 lg:col-span-4 lg:justify-end">
            <Link to="/test-ai" className="btn-lime">
              {t("testOurAI")}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            <Link to="/donate" className="btn-outline-light">
              Donate
            </Link>
          </div>
        </div>
      </div>

      <div className="container-x py-14 sm:py-16">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-4">
            <Link
              to="/"
              className="font-display text-[1.25rem] font-semibold tracking-[-0.02em] text-white"
            >
              AgriHyphen<span className="text-agri-lime"> AI</span>
            </Link>
            <p className="mt-4 max-w-[26ch] text-[0.9375rem] leading-[1.7] text-white/65">
              {t("agrihyphen_ai_desc")}
            </p>
            <div className="mt-7 flex items-center gap-2">
              <SocialLink href={CONTACT.linkedin} label="LinkedIn">
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                  <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9.5h4V21H3V9.5Zm6.5 0h3.84v1.57h.05c.54-1 1.85-2.07 3.81-2.07 4.07 0 4.82 2.68 4.82 6.16V21h-4v-5.07c0-1.21-.02-2.77-1.69-2.77-1.7 0-1.96 1.33-1.96 2.69V21h-4V9.5Z" />
                </svg>
              </SocialLink>
              <SocialLink href={CONTACT.whatsapp} label="WhatsApp">
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                  <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5.06-1.33A10 10 0 1 0 12 2Zm0 18.2a8.17 8.17 0 0 1-4.17-1.14l-.3-.18-3.1.81.83-3.02-.2-.31A8.2 8.2 0 1 1 12 20.2Zm4.5-6.14c-.24-.12-1.44-.71-1.66-.79-.22-.08-.39-.12-.55.12-.16.24-.63.79-.78.95-.14.16-.29.18-.53.06-.24-.12-1.02-.38-1.95-1.2-.72-.64-1.2-1.44-1.35-1.68-.14-.24-.02-.37.11-.49.11-.11.24-.29.36-.43.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.55-1.32-.75-1.8-.2-.48-.4-.41-.55-.42h-.47c-.16 0-.42.06-.64.3-.22.24-.85.83-.85 2.02 0 1.19.87 2.34.99 2.5.12.16 1.71 2.61 4.15 3.66.58.25 1.03.4 1.38.51.58.18 1.11.16 1.53.1.47-.07 1.44-.59 1.64-1.16.2-.57.2-1.06.14-1.16-.06-.1-.22-.16-.46-.28Z" />
                </svg>
              </SocialLink>
              <SocialLink href={CONTACT.website} label="Hyphen Tech website">
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
                </svg>
              </SocialLink>
            </div>
          </div>

          <nav className="lg:col-span-2 lg:col-start-6">
            <ColumnHeading>Company</ColumnHeading>
            <ul className="mt-4">
              {COMPANY_LINKS.map((link) => (
                <FooterLink key={link.to} to={link.to}>
                  {link.label}
                </FooterLink>
              ))}
            </ul>
          </nav>

          <nav className="lg:col-span-3">
            <ColumnHeading>Resources</ColumnHeading>
            <ul className="mt-4">
              {RESOURCE_LINKS.map((link) => (
                <FooterLink key={link.to} to={link.to}>
                  {link.label}
                </FooterLink>
              ))}
            </ul>
          </nav>

          <div className="lg:col-span-2">
            <ColumnHeading>{t("contactUs")}</ColumnHeading>
            <ul className="mt-4 space-y-1">
              <li>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="inline-block break-all py-1.5 text-[0.9375rem] text-white/65 transition-colors duration-200 hover:text-agri-lime"
                >
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${CONTACT.phone.replace(/\s+/g, "")}`}
                  className="inline-block py-1.5 text-[0.9375rem] tabular-nums text-white/65 transition-colors duration-200 hover:text-agri-lime"
                >
                  {CONTACT.phone}
                </a>
              </li>
              <li className="py-1.5 text-[0.9375rem] text-white/65">
                {CONTACT.location}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-2 border-t border-white/12 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-mono text-[0.6875rem] uppercase tracking-[0.1em] text-white/60">
            © {new Date().getFullYear()} AgriHyphen AI — {t("allRightsReserved")}
          </p>
          <p className="font-mono text-[0.6875rem] uppercase tracking-[0.1em] text-white/60">
            Built by Hyphen Tech · Ngozi, Burundi
          </p>
        </div>
      </div>
    </footer>
  );
}
